/**
 * EnvelopeRenderer (SR2 P1.4) — renders the CoPilot response envelopes
 * natively (PRD §14). The model chooses an envelope; the FE owns the visuals
 * (design tokens, a11y). Unknown/unhandled types degrade to nothing (the
 * message's streamed prose already carries the fallback text).
 *
 * P1 ships the text-adjacent envelopes; richer cards (task_card actions,
 * action_proposal confirmation) gain interactivity in P2/P3 on this surface.
 */

import { HeroIcon, type HeroType } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';
import type { CoPilotResponseEnvelope, CoPilotSource } from '@tonnie/types';

const HERO_KEYS: readonly HeroType[] = ['checkup', 'teleportal', 'qultureup', 'care', 'passport'];
/** Render the hero icon when the envelope's hero is one we have an icon for. */
function HeroBadge({ hero }: { hero: string }) {
  const key = hero.toLowerCase();
  if (!(HERO_KEYS as readonly string[]).includes(key)) return null;
  return <HeroIcon hero={key as HeroType} className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
}

function SourceAffordance({ source }: { source?: CoPilotSource }) {
  const { t } = useTranslation();
  if (!source) return null;
  const label =
    source.origin === 'case_base' && source.n
      ? t('copilot.source.cases', { n: source.n })
      : source.label;
  return (
    <p className="mt-1 text-2xs text-muted-foreground italic" title={t('copilot.source.why')}>
      {t('copilot.source.from')}: {label}
    </p>
  );
}

export function EnvelopeRenderer({ envelope }: { envelope: CoPilotResponseEnvelope }) {
  const { t } = useTranslation();

  switch (envelope.type) {
    case 'text':
      return (
        <div className="text-sm whitespace-pre-wrap">
          {envelope.text}
          <SourceAffordance source={envelope.source} />
        </div>
      );

    case 'checklist':
      return (
        <div className="text-sm">
          {envelope.title && <p className="font-medium mb-1">{envelope.title}</p>}
          <ul className="space-y-1">
            {envelope.items.map((item) => (
              <li key={item.id} className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs',
                    item.done ? 'text-primary-accessible' : 'text-muted-foreground'
                  )}
                >
                  {item.done ? '✓' : '○'}
                </span>
                <span className={cn(item.done && 'line-through text-muted-foreground')}>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
          <SourceAffordance source={envelope.source} />
        </div>
      );

    case 'kit_item':
      return (
        <div className="text-sm rounded-lg border border-border p-2">
          <p className="font-medium">{envelope.title}</p>
          <p className="text-muted-foreground text-xs">{envelope.summary}</p>
          <SourceAffordance source={envelope.source} />
        </div>
      );

    case 'task_card':
      return (
        <div className="text-sm rounded-lg border border-border p-2">
          <p className="flex items-center gap-1.5 font-medium">
            <HeroBadge hero={envelope.hero} />
            {envelope.title}
          </p>
          <p className="text-xs text-muted-foreground">
            {envelope.hero} · {envelope.status}
          </p>
          <SourceAffordance source={envelope.source} />
        </div>
      );

    case 'case_insight':
      return (
        <div className="text-sm rounded-lg bg-secondary/40 p-2">
          <p>{envelope.insight}</p>
          <p className="mt-1 text-2xs text-muted-foreground">
            {t('copilot.source.cases', { n: envelope.n })}
            {envelope.caveat ? ` · ${envelope.caveat}` : ''}
          </p>
        </div>
      );

    case 'document_summary':
      return (
        <div className="text-sm rounded-lg border border-border p-2">
          <p className="font-medium">{envelope.fileName}</p>
          <p className="text-muted-foreground">{envelope.summary}</p>
        </div>
      );

    case 'sources':
      // Web-search citations (BL-213 v2) — a "Sources" card like GPT. The URLs
      // come from the search, never the model. Open in a new tab, safely.
      if (envelope.sources.length === 0) return null;
      return (
        <div className="text-sm rounded-lg border border-border p-2">
          <p className="mb-1 text-2xs font-medium tracking-wide text-muted-foreground uppercase">
            {t('copilot.source.webSources')}
          </p>
          <ol className="space-y-1.5">
            {envelope.sources.map((s, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-2xs text-muted-foreground">{i + 1}.</span>
                <div className="min-w-0">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    data-testid="copilot-source-link"
                    className="text-primary-accessible underline hover:no-underline"
                  >
                    {s.title || s.url}
                  </a>
                  {s.snippet && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{s.snippet}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      );

    default:
      // score_explainer / comparison_table / problem_report / action_proposal /
      // escalation_card gain native rendering in P1.4+/P2 — the streamed prose
      // already carries a readable fallback until then.
      return null;
  }
}
